import cn from '../utils/cn.ts';
import { ContentDisplayStyle, useMapContext, ViewType } from './Map.tsx';
import {
  faChevronUp,
  faMagnifyingGlass,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '../common/Loading.tsx';
import { useContext, useEffect, useRef, useState } from 'react';
import { LanguageContext } from '@/common/Language.tsx';

export default function PanelContentFooter() {
  const { t } = useContext(LanguageContext);
  const {
    view,
    setContentDisplayStyle,
    shortContentRerender, // do not remove
    shortContentTimeout,
    resetShortContentTimeout,
  } = useMapContext();

  if (
    !view ||
    view.type !== ViewType.Regular ||
    view.contentStyle === ContentDisplayStyle.Hidden
  )
    return null;

  return (
    <div
      className={cn(
        'flex-[0_0_auto]',
        'flex',
        '-mx-[40px]',
        '-mb-[12px]',
        'text-center',
        'border-t-1',
        'border-t-gray-200',
        'rounded-b-[12px]',
        'overflow-hidden'
      )}
    >
      {view.contentStyle === ContentDisplayStyle.Full && (
        <div
          className={cn(
            'flex-1',
            'cursor-pointer',
            'p-[12px]',
            'border-t-1',
            'border-t-white',
            'bg-gray-200',
            'hover:bg-radial',
            'hover:from-gray-100',
            'hover:to-gray-200'
          )}
          onClick={() => {
            setContentDisplayStyle(ContentDisplayStyle.Hidden);
          }}
        >
          <FontAwesomeIcon icon={faChevronUp} className={'mr-[6px]'} />
          &nbsp;{t('HIDE', 'VERSTECKEN')}
        </div>
      )}
      {view.contentStyle === ContentDisplayStyle.Short && (
        <>
          <div
            className={cn(
              'flex-1',
              'cursor-pointer',
              'p-[12px]',
              'border-t-1',
              'border-t-white',
              'group',
              'bg-gray-200',
              'hover:bg-radial',
              'hover:from-gray-100',
              'hover:to-gray-200'
            )}
            onClick={() => {
              if (shortContentTimeout > 0) resetShortContentTimeout();
              else setContentDisplayStyle(ContentDisplayStyle.Hidden);
            }}
          >
            <span
              className={cn(shortContentTimeout ? 'group-hover:hidden' : null)}
            >
              <FontAwesomeIcon icon={faChevronUp} className={'mr-[6px]'} />
              &nbsp;{t('HIDE', 'VERSTECKEN')}
              {shortContentTimeout > 0 && (
                <>
                  &nbsp;in&nbsp;
                  <TimerComponent startAt={3} />s
                </>
              )}
            </span>
            {shortContentTimeout > 0 && (
              <span className={cn('hidden', 'group-hover:inline')}>
                <FontAwesomeIcon icon={faTimes} className={'mr-[6px]'} />
                &nbsp;{t('KEEP', 'HALTEN')}
              </span>
            )}
          </div>
          <div
            className={cn(
              'flex-1',
              'cursor-pointer',
              'p-[12px]',
              'border-t-1',
              'border-t-white',
              'border-l-1',
              'border-l-white',
              'bg-gray-200',
              'hover:bg-radial',
              'hover:from-gray-100',
              'hover:to-gray-200'
            )}
            onClick={() => {
              setContentDisplayStyle(ContentDisplayStyle.Full);
            }}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} className={'mr-[6px]'} />
            {t('READ MORE', 'MEHR LESEN')}
          </div>
        </>
      )}
    </div>
  );
}

function TimerComponent({ startAt }: { startAt: number }) {
  const [seconds, setSeconds] = useState(startAt);
  const intervalRef = useRef<number>(0);

  // Start timer on mount
  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      // This is safe: using functional update ensures latest value
      setSeconds((prev) => prev - 1);
    }, 1000);

    // Cleanup on unmount
    return () => clearInterval(intervalRef.current);
  }, []);

  return <>{seconds}</>;
}
